import React from 'react';
import styled from 'styled-components';

import { TOTAL_ROUNDS } from './constants';

// Design language matches src/components/GameHUD/GameHUD.tsx (panorama games)
// adapted to light theme since quizzes render on a white QuizPanel.
const Hud = styled.div`
    display: flex;
    gap: 12px;
    align-items: stretch;

    @media (max-width: 640px) {
        flex-direction: column;
    }
`;

const HudCard = styled.div`
    background: #ffffff;
    border: 1px solid #f0f0f0;
    border-radius: 12px;
    padding: 14px 16px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
`;

const RoundCard = styled(HudCard)`
    display: flex;
    align-items: center;
    gap: 14px;
`;

const RoundBadge = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    background: linear-gradient(135deg, #c80707 0%, #a00606 100%);
    border-radius: 10px;
    font-family: 'JetBrains Mono', 'SF Mono', 'Consolas', monospace;
    font-size: 18px;
    font-weight: 700;
    color: #ffffff;
    box-shadow: 0 4px 12px rgba(200, 7, 7, 0.25);
    flex-shrink: 0;
`;

const HudInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const HudLabel = styled.span`
    font-family: 'Work Sans', sans-serif;
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #8c8c8c;
`;

const HudText = styled.span`
    font-family: 'Work Sans', sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: #1f1f1f;
`;

const ScoreCard = styled(HudCard)`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 200px;
`;

const ScoreHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const ScoreHeaderMeta = styled.span`
    margin-left: auto;
    color: #8c8c8c;
    font-size: 13px;
    font-weight: 500;
    text-align: right;

    @media (max-width: 640px) {
        margin-left: 0;
        width: 100%;
        text-align: left;
    }
`;

const ScoreIcon = styled.div`
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #c80707;
`;

const ScoreValue = styled.div`
    font-family: 'JetBrains Mono', 'SF Mono', 'Consolas', monospace;
    font-size: 28px;
    font-weight: 700;
    color: #1f1f1f;
    letter-spacing: -0.02em;
    line-height: 1.05;
`;

const ScoreUnit = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: #8c8c8c;
    margin-left: 6px;
`;

const ProgressBar = styled.div`
    width: 100%;
    height: 4px;
    background: #f0f0f0;
    border-radius: 2px;
    overflow: hidden;
    margin-top: 2px;
`;

const ProgressFill = styled.div`
    height: 100%;
    width: ${({ $percent }) => $percent}%;
    background: linear-gradient(90deg, #c80707 0%, #ff4444 100%);
    border-radius: 2px;
    transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
`;

export const RoundStatus = ({
    round,
    score,
    maxScore,
    totalRounds = TOTAL_ROUNDS,
    scoreLabel = 'Celkové skóre',
    scoreUnit = 'bodů',
}) => {
    const percent = maxScore > 0 ? Math.min(Math.round((score / maxScore) * 100), 100) : 0;

    return (
        <Hud>
            <RoundCard>
                <RoundBadge>{round}</RoundBadge>
                <HudInfo>
                    <HudLabel>Kolo</HudLabel>
                    <HudText>
                        {round} / {totalRounds}
                    </HudText>
                </HudInfo>
            </RoundCard>

            <ScoreCard>
                <ScoreHeader>
                    <ScoreIcon>
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                    </ScoreIcon>
                    <HudLabel>{scoreLabel}</HudLabel>
                </ScoreHeader>
                <ScoreValue>
                    {score}
                    <ScoreUnit>
                        / {maxScore} {scoreUnit}
                    </ScoreUnit>
                </ScoreValue>
                <ProgressBar>
                    <ProgressFill $percent={percent} />
                </ProgressBar>
            </ScoreCard>
        </Hud>
    );
};

export const StreakStatus = ({ streak, bestStreak, poolSize, minPopulation }) => {
    const percent = bestStreak > 0 ? Math.min(Math.round((streak / bestStreak) * 100), 100) : 0;

    return (
        <Hud>
            <RoundCard>
                <RoundBadge>{streak}</RoundBadge>
                <HudInfo>
                    <HudLabel>Streak</HudLabel>
                    <HudText>{streak} správně v řadě</HudText>
                </HudInfo>
            </RoundCard>

            <ScoreCard>
                <ScoreHeader>
                    <ScoreIcon>
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M8 21h8" />
                            <path d="M12 17v4" />
                            <path d="M7 4h10v5a5 5 0 0 1-10 0V4z" />
                            <path d="M5 6H3a3 3 0 0 0 3 3h1" />
                            <path d="M19 6h2a3 3 0 0 1-3 3h-1" />
                        </svg>
                    </ScoreIcon>
                    <HudLabel>Nejlepší výsledek</HudLabel>
                    {poolSize && minPopulation && (
                        <ScoreHeaderMeta>
                            Z {poolSize.toLocaleString('cs-CZ')} obcí ČR (≥ {minPopulation})
                        </ScoreHeaderMeta>
                    )}
                </ScoreHeader>
                <ScoreValue>
                    {bestStreak}
                    <ScoreUnit>best streak</ScoreUnit>
                </ScoreValue>
                <ProgressBar>
                    <ProgressFill $percent={percent} />
                </ProgressBar>
            </ScoreCard>
        </Hud>
    );
};
